import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { galleryItems, type GalleryItem } from './galleryConfig'
import ProgressiveImage from '../../components/progressiveImage/ProgressiveImage'

import './gallery.scss'

function Gallery() {
  const { t } = useTranslation()
  const [selectedItem, setSelectedItem] = useState<GalleryItem | null>(null)
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({})

  useEffect(() => {
    document.title = 'Gallery | Dwain'
  }, [])

  const handleImageLoad = (itemId: number, loaded: boolean) => {
    setLoadedImages(prev => ({ ...prev, [itemId]: loaded }))
  }

  const handleItemClick = (item: GalleryItem) => {
    // Nur öffnen wenn das Bild vollständig geladen ist
    if (loadedImages[item.id]) {
      setSelectedItem(item)
    }
  }

  const handleCloseModal = () => {
    setSelectedItem(null)
  }

  const handleNextItem = () => {
    if (selectedItem) {
      const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id)
      const nextIndex = (currentIndex + 1) % galleryItems.length
      setSelectedItem(galleryItems[nextIndex])
    }
  }

  const handlePrevItem = () => {
    if (selectedItem) {
      const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id)
      const prevIndex = currentIndex === 0 ? galleryItems.length - 1 : currentIndex - 1
      setSelectedItem(galleryItems[prevIndex])
    }
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedItem) return

      if (e.key === 'ArrowRight') {
        const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id)
        const nextIndex = (currentIndex + 1) % galleryItems.length
        setSelectedItem(galleryItems[nextIndex])
      } else if (e.key === 'ArrowLeft') {
        const currentIndex = galleryItems.findIndex(item => item.id === selectedItem.id)
        const prevIndex = currentIndex === 0 ? galleryItems.length - 1 : currentIndex - 1
        setSelectedItem(galleryItems[prevIndex])
      } else if (e.key === 'Escape') {
        setSelectedItem(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedItem])

  return (
    <main className='page-gallery'>
      <div className='gallery-content'>
        <header className='content-header'>
          <h1 className='header-title'>{t('gallery.title')}</h1>
          <p className='header-subtitle'>{t('gallery.subtitle')}</p>
          <p className='header-timespan'>{t('gallery.timespan')}</p>
        </header>

        {galleryItems.length === 0 ? (
          <div className='gallery-empty'>
            <p>{t('gallery.empty')}</p>
          </div>
        ) : (
          <section className='gallery-grid' aria-label="Gallery grid">
            {galleryItems.map((item) => (
              <div
                key={item.id}
                className={`gallery-item ${loadedImages[item.id] ? 'gallery-item--loaded' : 'gallery-item--loading'}`}
                onClick={() => handleItemClick(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleItemClick(item)}
              >
                {item.type === 'image' ? (
                  <ProgressiveImage
                    src={item.src}
                    thumbnail={item.thumbnail || ''}
                    alt={item.title || 'Gallery image'}
                    className='item-preview'
                    onLoad={(loaded) => handleImageLoad(item.id, loaded)}
                  />
                ) : (
                  <div className='item-video-preview'>
                    <ProgressiveImage
                      src={item.thumbnail || item.src}
                      thumbnail={item.thumbnail || ''}
                      alt={item.title || 'Video thumbnail'}
                      className='item-preview'
                      onLoad={(loaded) => handleImageLoad(item.id, loaded)}
                    />
                    <div className='video-overlay'>
                      <svg className='play-icon' viewBox='0 0 24 24' fill='currentColor'>
                        <path d='M8 5v14l11-7z' />
                      </svg>
                    </div>
                  </div>
                )}
                {item.title && (
                  <div className='item-info'>
                    <h3 className='item-title'>
                      {item.category && <span className='title-category'>{item.category} </span>}
                      {item.title}
                    </h3>
                    {item.description && <p className='item-description'>{item.description}</p>}
                  </div>
                )}
                <div className='item-bottom'>
                  <div className='bottom-bar'></div>
                </div>
              </div>
            ))}
          </section>
        )}
      </div>

      {selectedItem && (
        <div className='gallery-modal'>
          <button
            className='modal-nav modal-nav-prev'
            onClick={handlePrevItem}
            aria-label="Previous image"
          >
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z' />
            </svg>
          </button>

          <div className='modal-content'>
            <button className='modal-close' onClick={handleCloseModal} aria-label="Close modal">
              <svg viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
              </svg>
            </button>

            {selectedItem.type === 'image' ? (
              <img src={selectedItem.src} alt={selectedItem.title || 'Gallery image'} className='modal-media' />
            ) : (
              <video src={selectedItem.src} controls className='modal-media' autoPlay>
                Your browser does not support the video tag.
              </video>
            )}

            {(selectedItem.title || selectedItem.description) && (
              <div className='modal-info'>
                {selectedItem.title && (
                  <h2 className='modal-title'>
                    {selectedItem.category && <span className='title-category'>{selectedItem.category} </span>}
                    {selectedItem.title}
                  </h2>
                )}
                {selectedItem.description && <p className='modal-description'>{selectedItem.description}</p>}
              </div>
            )}
          </div>

          <button
            className='modal-nav modal-nav-next'
            onClick={handleNextItem}
            aria-label="Next image"
          >
            <svg viewBox='0 0 24 24' fill='currentColor'>
              <path d='M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z' />
            </svg>
          </button>
        </div>
      )}
    </main>
  )
}

export default Gallery
